import { CompanyRole } from 'modules/models/project-new';
import { IFormRules } from 'utils';
import { validateRules } from '../../utils/useValidator';
import { isEmpty, parseFloatNumber, sanitizeNumber } from '../../utils/generalUtils';
import { AddressModel, ProjectNewModel, TurnstileScheduleModel } from 'modules/models';
import { ILocation } from 'modules/models/address';
import { CompanyStatus } from 'modules/models/project-new';
import { ICertificationGroup, IProjectCertification } from 'modules/models/certification';
import { IProjectTraining, ITrainingGroup } from 'modules/models/training';
import { IConsentFormLegal } from 'modules/models/consentForm';
import { IFcaLearning, IFcaLearningGroup } from 'modules/models/fcaLearning';
import { ISendCommunicationResponse } from 'modules/models/communications';
import { IWorkerProfileField } from 'modules/models/workerProfile';
import { GroupValidationType } from 'modules/models/general';
import { BadgePrintForType } from 'modules/models/resource';

export const canadaRegex = new RegExp(/^[a-zA-Z]{1}[0-9]{1}[a-zA-Z]{1}-[0-9]{1}[a-zA-Z]{1}[0-9]{1}$/);
export const zipRegex = new RegExp(/^[0-9]{5}-[0-9]{4}$|^[0-9]{5}$/);

export const validateBadge = (value: number, total: number = 1000, isSanitze = false) =>
  value && (isSanitze ? sanitizeNumber(value) >= total : parseFloatNumber(value) >= total);

const validateZipCode = ({ value }) => {
  const sanitizedValue = value?.length === 6 ? value?.replace('-', '').replace(' ', '') : value;
  const isInvalid = !zipRegex.test(sanitizedValue) && !canadaRegex.test(sanitizedValue);
  return value && value.length > 0 && isInvalid && 'Zip Code format is invalid.';
};

const validateBadgePrice = value => validateBadge(value) && 'Sub Badge price should be less than $1,000.';
const validateReprintingCost = value => validateBadge(value) && 'Sub Badge reprint price should be less than $1,000.';
const validateGCBadgePrice = value => validateBadge(value) && 'GC/Owner Badge price should be less than $1,000.';
const validateGCReprintingCost = value => validateBadge(value) && 'GC/Owner Badge reprint price should be less than $1,000.';
const validateSeatPrice = value => validateBadge(value) && 'Seat price should be less than $1,000.';

const getRequiredBadgeModelRules = ({ isBilledPerCompany }: { isBilledPerCompany: boolean }) => ({
  badgePrice: {
    required: true,
    rules: [({ value }) => validateBadgePrice(value)],
  },
  reprintingCost: {
    required: true,
    rules: [({ value }) => validateReprintingCost(value)],
  },
  gcBadgePrintPrice: {
    required: true,
    rules: [({ value }) => validateGCBadgePrice(value)],
  },
  gcBadgeReprintPrice: {
    required: true,
    rules: [({ value }) => validateGCReprintingCost(value)],
  },
  billedCompanyId: {
    required: false,
  },
  visitorBadgePrice: {
    required: true,
    rules: [],
  },
  visitorReprintingCost: {
    required: true,
    rules: [],
  },
});

const getRequiredSeatModelRules = ({ isFixedSeatPrice }: { isFixedSeatPrice: boolean }) => ({
  estimatedWorkersNumber: {
    required: true,
    rules: [],
  },
  seatPrice: {
    required: isFixedSeatPrice,
    rules: [({ value }) => validateSeatPrice(value)],
  },
  billedCompanyId: {
    required: false,
    rules: [],
  },
  reprintingCost: {
    required: true,
    rules: [({ value }) => validateReprintingCost(value)],
  },
  visitorBadgePrice: {
    required: true,
    rules: [],
  },
  visitorReprintingCost: {
    required: true,
    rules: [],
  },
});

export const validateBadgeBillingModel = (
  // { value: badgeModel, skipSubcontractorsPaymentMethods, skipPaymentMethodSubcontractors }: { value: any },
  { model, value },
  rules: IFormRules
) => {
  // const value = model.model;
  let err = validateRules({ model: value, constraints: rules });

  if (
    model.skipSubcontractorsPaymentMethods &&
    (!model.skipPaymentMethodSubcontractors || model.skipPaymentMethodSubcontractors.length < 1) &&
    !model.skipPaymentMethodForAllSubcontractors
  ) {
    if (err) {
      err.skipPaymentMethodSubcontractors = 'is required';
    } else {
      err = {
        skipPaymentMethodSubcontractors: 'is required',
      };
    }
  }
  const responsibleForBillingErr: boolean = validateBadgeResponsibleForBilling(value.billingModelResponsibleCompanies);
  if (responsibleForBillingErr) {
    if (err) {
      err.billedCompanyId = 'is required';
    } else {
      err = {
        billedCompanyId: 'is required',
      };
    }
  }
  const badgeErrorList = Object.keys(err).reduce((total: any, field, index) => ({ ...total, [field]: err[field] }), {});
  return Object.keys(err).length > 0 ? badgeErrorList : false;
};

const validateBadgeResponsibleForBilling = responsibleCompanies => {
  if (
    responsibleCompanies &&
    responsibleCompanies.length > 0 &&
    responsibleCompanies.filter(bmrc => bmrc.badgePrintForType === BadgePrintForType.SubcontractorBadgePrint).length > 0 &&
    responsibleCompanies.filter(bmrc => bmrc.badgePrintForType === BadgePrintForType.SubcontractorBadgeReprint).length > 0 &&
    responsibleCompanies.filter(bmrc => bmrc.badgePrintForType === BadgePrintForType.GcOwnerBadgePrint).length > 0 &&
    responsibleCompanies.filter(bmrc => bmrc.badgePrintForType === BadgePrintForType.GcOwnerBadgeReprint).length > 0 &&
    responsibleCompanies.filter(bmrc => bmrc.badgePrintForType === BadgePrintForType.VisitorBadgePrint).length > 0 &&
    responsibleCompanies.filter(bmrc => bmrc.badgePrintForType === BadgePrintForType.VisitorBadgeReprint).length > 0
  ) {
    return (
      responsibleCompanies.filter(
        bmrc =>
          (!bmrc.isBilledPerCompany && (!bmrc.billedCompanyId || bmrc.billedCompanyId.length < 1)) ||
          // (bmrc.isBilledPerCompany && !bmrc.isClientsCoveredOtherClients) ||
          (bmrc.isClientsCoveredOtherClients &&
            (!bmrc.billedCompanyId || bmrc.billedCompanyId.length < 1 || !bmrc.coveredCompanyId || bmrc.coveredCompanyId.length < 1))
      ).length > 0
    );
  } else {
    return true;
  }
};

export const validateSeatBillingModel = ({ value: seatModel }: { value: ProjectNewModel.ISeatsModel }, rules: IFormRules) => {
  const err = validateRules({ model: seatModel, constraints: rules });
  const seatErrorList = Object.keys(err).reduce((total: any, field, index) => ({ ...total, [field]: err[field] }), {});
  return Object.keys(err).length > 0 ? seatErrorList : false;
};

export const validateRequiredBadgeBillingModel = value =>
  validateBadgeBillingModel(value, getRequiredBadgeModelRules({ isBilledPerCompany: value.value?.isBilledPerCompany }));

export const validateRequiredSeatBillingModel = value =>
  validateSeatBillingModel(value, getRequiredSeatModelRules({ isFixedSeatPrice: value.value?.isFixedSeatPrice }));

export const validateAddress = ({ value: addressModel }: { value: AddressModel.IAddress }) => {
  const err = validateRules({ model: addressModel, constraints: addressRules });
  const addressModelList = Object.keys(err).reduce((total: any, field, index) => ({ ...total, [field]: err[field] }), {});
  return Object.keys(err).length > 0 ? addressModelList : false;
};

export const validateRequiredAddress = ({ value: addressModel }: { value: AddressModel.IAddress }) => {
  const err = validateRules({ model: addressModel, constraints: requiredAddressRules });
  const addressModelList = Object.keys(err).reduce((total: any, field, index) => ({ ...total, [field]: err[field] }), {});
  return Object.keys(err).length > 0 ? addressModelList : false;
};

const isDuplicatedLocation = (location: ILocation, locations: ILocation[]) => locations.filter(locationItem => locationItem.name === location.name).length > 0;

const validateLocationList = ({ value: locationList, model }: { value: ILocation[]; model: any }) => {
  const userErrorList = locationList.reduce((acc: any, locations, index) => {
    const isDuplicated =
      !isEmpty(locations.name) &&
      isDuplicatedLocation(
        locations,
        model.locations.filter((u, i) => i !== index)
      );
    if (isDuplicated) {
      acc[`locations[${index}].name`] = 'The same Location Address cannot be used.';
    }

    if (isEmpty(locations.name)) {
      acc[`locations[${index}].name`] = 'Location Name cannot be empty.';
    }

    if (locations?.locationCodes?.length > 0) {
      locations?.locationCodes?.forEach((code, codeIndex) => {
        const isDuplicated = locations?.locationCodes?.filter((_, i) => i !== codeIndex).find(u => u.name === code.name);
        if (isDuplicated) {
          acc[`locations[${index}].locationCodes[${codeIndex}].name`] = 'The same Location Code cannot be used.';
        }
        if (isEmpty(code.name)) {
          acc[`locations[${index}].locationCodes[${codeIndex}].name`] = 'Location Code cannot be empty.';
        }
        return acc;
      });
    }

    return acc;
  }, {});
  return Object.keys(userErrorList).length > 0 ? userErrorList : false;
};

const validateAddressList = ({ value: addressList, model }: { value: AddressModel.IAddress[]; model: any }) => {
  const userErrorList = (addressList || []).reduce((acc: any, address, index) => {
    if (validateRequiredAddress({ value: address })) {
      const errors = validateRequiredAddress({ value: address });
      Object.keys(errors).forEach(key => {
        acc[index] = {
          ...acc[index],
          [key]: errors[key],
        };
      });
    }

    return acc;
  }, {});
  return Object.keys(userErrorList).length > 0 ? userErrorList : false;
};

const validateCertificationAndTrainingGroupName = (name: string, groupNameList: string[]) => {
  if (!isEmpty(name)) {
    if (groupNameList.includes(name)) {
      return 'Group Name is already in use.';
    } else {
      groupNameList.push(name);
      return false;
    }
  }
  return 'Please enter a Group Name';
};

const validateCertificationAndTrainingGroupItems = (
  items: IProjectCertification[] | IProjectTraining[] | IFcaLearning[],
  entity: 'Certification' | 'Training' | 'Course',
  getErrorKeyFn: (index: number) => string,
  itemsInUseList: string[],
  aliasInUseList: string[]
) => {
  const errors = {};
  items.forEach((item, itemIndex) => {
    const key = getErrorKeyFn(itemIndex);
    if (itemsInUseList.includes(item[entity === 'Course' ? 'courseId' : 'id'])) {
      errors[key + (entity === 'Course' ? 'courseId' : 'id')] = `${entity} is already in use`;
    } else {
      itemsInUseList.push(item[entity === 'Course' ? 'courseId' : 'id']);
    }

    if (aliasInUseList.includes(item.alias) && item.alias !== '' && !!item.alias) {
      errors[key + 'alias'] = `Alias is already in use.`;
    } else {
      aliasInUseList.push(item.alias);
    }
  });
  return errors;
};

export const validateCertificationGroupsList = ({ value: groupsList }: { value: ICertificationGroup[] }) => {
  const groupNameList = [];
  const certificationsInUseList = [];
  const aliasInUseList = [];

  const errorList = groupsList.reduce((errors: any, group, groupIndex) => {
    const nameError = validateCertificationAndTrainingGroupName(group.name, groupNameList);
    if (nameError) {
      errors[`certificationGroups[${groupIndex}].name`] = nameError;
    }

    if (isEmpty(group.validationType)) {
      errors[`certificationGroups[${groupIndex}].validationType`] = 'Please select a Validation Type';
    }

    if (
      [GroupValidationType.ALL_MANDATORY, GroupValidationType.AT_LEAST_ONE].includes(group.validationType) &&
      !group.newWorkerGracePeriodInDays &&
      group.newWorkerGracePeriodInDays !== 0 &&
      !group.newWorkerMandatoryDeadline
    ) {
      errors[`certificationGroups[${groupIndex}].newWorkerMandatoryDeadline`] = 'Mandatory dealine is required.';
      errors[`certificationGroups[${groupIndex}].newWorkerGracePeriodInDays`] = 'Grace period is required.';
    }

    if (group.newWorkerGracePeriodInDays < 0) {
      errors[`certificationGroups[${groupIndex}].newWorkerGracePeriodInDays`] = 'Please enter positive number of days.';
    }

    if (
      [GroupValidationType.ALL_MANDATORY, GroupValidationType.AT_LEAST_ONE].includes(group.validationType) &&
      !group.existingWorkerGracePeriodInDays &&
      group.existingWorkerGracePeriodInDays !== 0 &&
      !group.existingWorkerMandatoryDeadline
    ) {
      errors[`certificationGroups[${groupIndex}].existingWorkerMandatoryDeadline`] = 'Mandatory dealine is required.';
      errors[`certificationGroups[${groupIndex}].existingWorkerGracePeriodInDays`] = 'Grace period is required.';
    }

    if (group.existingWorkerGracePeriodInDays < 0) {
      errors[`certificationGroups[${groupIndex}].existingWorkerGracePeriodInDays`] = 'Please enter positive number of days.';
    }

    if (isEmpty(group.certifications)) {
      errors[`certificationGroups[${groupIndex}].certifications`] = 'Please select at least one Certification';
    } else {
      const getErrorKey = (itemIndex: number) => `certificationGroups[${groupIndex}].certifications[${itemIndex}].`;
      const certificationErrors = validateCertificationAndTrainingGroupItems(
        group.certifications,
        'Certification',
        getErrorKey,
        certificationsInUseList,
        aliasInUseList
      );
      if (Object.keys(certificationErrors).length > 0) {
        errors = { ...errors, ...certificationErrors };
      }
    }
    return errors;
  }, {});
  return Object.keys(errorList).length > 0 ? errorList : false;
};

export const validateTrainingGroupsList = ({ value: groupsList }: { value: ITrainingGroup[] }) => {
  const groupNameList = [];
  const trainingsInUseList = [];
  const aliasInUseList = [];

  const errorList = groupsList.reduce((errors: any, group, groupIndex) => {
    const nameError = validateCertificationAndTrainingGroupName(group.name, groupNameList);
    if (nameError) {
      errors[`trainingGroups[${groupIndex}].name`] = nameError;
    }

    if (isEmpty(group.validationType)) {
      errors[`trainingGroups[${groupIndex}].validationType`] = 'Please select a Validation Type';
    }

    if (
      [GroupValidationType.ALL_MANDATORY, GroupValidationType.AT_LEAST_ONE].includes(group.validationType) &&
      !group.newWorkerMandatoryDeadline &&
      !group.newWorkerGracePeriodInDays &&
      group.newWorkerGracePeriodInDays !== 0
    ) {
      errors[`trainingGroups[${groupIndex}].newWorkerMandatoryDeadline`] = 'Mandatory dealine is required.';
      errors[`trainingGroups[${groupIndex}].newWorkerGracePeriodInDays`] = 'Grace period is required.';
    }

    if (group.newWorkerGracePeriodInDays < 0) {
      errors[`trainingGroups[${groupIndex}].newWorkerGracePeriodInDays`] = 'Please enter valid number of days.';
    }

    if (
      [GroupValidationType.ALL_MANDATORY, GroupValidationType.AT_LEAST_ONE].includes(group.validationType) &&
      !group.existingWorkerGracePeriodInDays &&
      group.existingWorkerGracePeriodInDays !== 0 &&
      !group.existingWorkerMandatoryDeadline
    ) {
      errors[`trainingGroups[${groupIndex}].existingWorkerMandatoryDeadline`] = 'Mandatory dealine is required.';
      errors[`trainingGroups[${groupIndex}].existingWorkerGracePeriodInDays`] = 'Grace period is required.';
    }

    if (group.existingWorkerGracePeriodInDays < 0) {
      errors[`trainingGroups[${groupIndex}].existingWorkerGracePeriodInDays`] = 'Please enter positive number of days.';
    }

    if (isEmpty(group.trainings)) {
      errors[`trainingGroups[${groupIndex}].trainings`] = 'Please select at least one Training';
    } else {
      const getErrorKey = (itemIndex: number) => `trainingGroups[${groupIndex}].trainings[${itemIndex}].`;
      const trainingsErrors = validateCertificationAndTrainingGroupItems(group.trainings, 'Training', getErrorKey, trainingsInUseList, aliasInUseList);
      if (Object.keys(trainingsErrors).length > 0) {
        errors = { ...errors, ...trainingsErrors };
      }
    }
    return errors;
  }, {});
  return Object.keys(errorList).length > 0 ? errorList : false;
};

export const validateFcaLearningGroupsList = ({ value: groupsList }: { value: IFcaLearningGroup[] }) => {
  const groupNameList = [];
  const coursesInUseList = [];
  const aliasInUseList = [];

  const errorList = groupsList?.reduce((errors: any, group, groupIndex) => {
    const nameError = validateCertificationAndTrainingGroupName(group.name, groupNameList);
    if (nameError) {
      errors[`fcaLearningGroups[${groupIndex}].name`] = nameError;
    }

    if (isEmpty(group.validationType)) {
      errors[`fcaLearningGroups[${groupIndex}].validationType`] = 'Please select a Validation Type';
    }

    if (
      [GroupValidationType.ALL_MANDATORY, GroupValidationType.AT_LEAST_ONE].includes(group.validationType) &&
      !group.newWorkerMandatoryDeadline &&
      !group.newWorkerGracePeriodInDays &&
      group.newWorkerGracePeriodInDays !== 0
    ) {
      errors[`fcaLearningGroups[${groupIndex}].newWorkerMandatoryDeadline`] = 'Mandatory dealine is required.';
      errors[`fcaLearningGroups[${groupIndex}].newWorkerGracePeriodInDays`] = 'Grace period is required.';
    }

    if (group.newWorkerGracePeriodInDays < 0) {
      errors[`fcaLearningGroups[${groupIndex}].newWorkerGracePeriodInDays`] = 'Please enter positive number of days.';
    }

    if (
      [GroupValidationType.ALL_MANDATORY, GroupValidationType.AT_LEAST_ONE].includes(group.validationType) &&
      !group.existingWorkerGracePeriodInDays &&
      group.existingWorkerGracePeriodInDays !== 0 &&
      !group.existingWorkerMandatoryDeadline
    ) {
      errors[`fcaLearningGroups[${groupIndex}].existingWorkerMandatoryDeadline`] = 'Mandatory dealine is required.';
      errors[`fcaLearningGroups[${groupIndex}].existingWorkerGracePeriodInDays`] = 'Grace period is required.';
    }

    if (group.existingWorkerGracePeriodInDays < 0) {
      errors[`fcaLearningGroups[${groupIndex}].existingWorkerGracePeriodInDays`] = 'Please enter positive number of days.';
    }

    if (isEmpty(group.learningGroupCourses)) {
      errors[`fcaLearningGroups[${groupIndex}].learningGroupCourses`] = 'Please select at least one Course';
    } else {
      const getErrorKey = (itemIndex: number) => `fcaLearningGroups[${groupIndex}].learningGroupCourses[${itemIndex}].`;
      const learningGroupCoursesErrors = validateCertificationAndTrainingGroupItems(
        group.learningGroupCourses,
        'Course',
        getErrorKey,
        coursesInUseList,
        aliasInUseList
      );
      if (Object.keys(learningGroupCoursesErrors).length > 0) {
        errors = { ...errors, ...learningGroupCoursesErrors };
      }
    }
    return errors;
  }, {});
  return Object.keys(errorList).length > 0 ? errorList : false;
};

const validateCommunicationResponses = ({ value: communicationResponses }: { value: ISendCommunicationResponse[] }) => {
  const errorList = communicationResponses.reduce((errors: any, response, responseIndex) => {
    if (isEmpty(response?.body) && !response?.allowFreeText) {
      errors[`communicationResponses[${responseIndex}].body`] = 'is required';
    }
    return errors;
  }, {});
  return Object.keys(errorList).length > 0 ? errorList : false;
};

export const fieldRules: IFormRules = {
  name: {
    required: true,
    rules: [],
  },
};

export const GeneralInformationLabelRules: IFormRules = {
  name: {
    required: true,
  },
  description: {
    required: false,
  },
  categoryId: {
    required: true,
  },
  ccv: {
    required: false,
  },
  regionId: {
    required: true,
  },
  typeId: {
    required: true,
  },
  subTypeId: {
    required: true,
  },
  naeId: {
    required: true,
  },
  fcaProjectDirector: {
    required: false,
  },
  startDate: {
    required: true,
  },
  endDate: {
    required: true,
  },
  plannedMonths: {
    required: false,
  },
  timeZoneId: {
    required: true,
  },
  mustComplyWithNycLL196: {
    required: false,
  },
  permitHolder: {
    required: false,
  },
  permitNumber: {
    required: false,
  },
  licenseNumber: {
    required: false,
  },
};

export const GeneralInformationDraftRules: IFormRules = {
  ...fieldRules,
  name: {
    required: true,
    rules: [],
  },
  description: {
    required: false,
    rules: [],
  },
  categoryId: {
    required: false,
    rules: [],
  },
  ccv: {
    required: false,
    rules: [],
  },
  regionId: {
    required: false,
    rules: [],
  },
  typeId: {
    required: false,
    rules: [],
  },
  subTypeId: {
    required: false,
    rules: [],
  },
  naeId: {
    required: false,
    rules: [],
  },
  fcaProjectDirector: {
    required: false,
    rules: [],
  },
  startDate: {
    required: false,
    rules: [],
  },
  endDate: {
    required: false,
    rules: [],
  },
  plannedMonths: {
    required: false,
    rules: [],
  },
  timeZoneId: {
    required: false,
    rules: [],
  },
  mustComplyWithNycLL196: {
    required: false,
    rules: [],
  },
  permitHolder: {
    required: false,
    rules: [],
  },
  permitNumber: {
    required: false,
    rules: [],
  },
  licenseNumber: {
    required: false,
    rules: [],
  },
};

export const GeneralInformationApprovalRules: IFormRules = {
  ...fieldRules,
  name: {
    required: true,
    rules: [],
  },
  description: {
    required: false,
    rules: [],
  },
  categoryId: {
    required: true,
    rules: [],
  },
  ccv: {
    required: false,
    rules: [],
  },
  regionId: {
    required: true,
    rules: [],
  },
  typeId: {
    required: true,
    rules: [],
  },
  subTypeId: {
    required: true,
    rules: [],
  },
  naeId: {
    required: true,
    rules: [],
  },
  fcaProjectDirector: {
    required: false,
    rules: [],
  },
  startDate: {
    required: true,
    rules: [],
  },
  endDate: {
    required: true,
    rules: [],
  },
  plannedMonths: {
    required: false,
    rules: [],
  },
  timeZoneId: {
    required: true,
    rules: [],
  },
  mustComplyWithNycLL196: {
    required: false,
    rules: [],
  },
  permitHolder: {
    required: false,
    rules: [],
  },
  permitNumber: {
    required: false,
    rules: [],
  },
  licenseNumber: {
    required: false,
    rules: [],
  },
  managedByUsers: {
    required: true,
    rules: [],
  }
};

export const getGeneralInformationApprovalRules = ({ defaultLocationManagerFlushHours }) => {
  return {
    ...GeneralInformationApprovalRules,
    clearLocationManagerAfterHrs: {
      required: defaultLocationManagerFlushHours ? true : false,
      rules: [
        ({ value, model }) => {
          return model?.defaultLocationManagerFlushHours && !(Number(value) > 0) && 'is required';
        },
      ],
    },
  };
};

export const getBillingModelDraftRules = () => ({
  ...fieldRules,
  billingModelType: {
    required: false,
    rules: [],
  },
  badgeBillingModel: {
    required: false,
    rules: [validateRequiredBadgeBillingModel],
  },
  seatBillingModel: {
    required: false,
    rules: [],
  },
});

export const getBillingModelApprovalRules = ({ billingModelType }) => ({
  ...fieldRules,
  billingModelType: {
    required: true,
    rules: [],
  },
  badgeBillingModel: {
    required: false,
    rules: [validateRequiredBadgeBillingModel],
  },
  // seatBillingModel: {
  //   required: billingModelType === ProjectNewModel.BillingModelType.SEATS,
  //   rules: billingModelType === ProjectNewModel.BillingModelType.SEATS ? [validateRequiredSeatBillingModel] : [],
  // },
});

export const getPricingAgreementDraftRules = () => ({
  ...fieldRules,
});

export const getPricingAgreementApprovalRules = ({ billingModelType }) => ({
  ...fieldRules,
});

export const BadgeBillingModelLabelRules: IFormRules = {
  badgePrice: {
    required: true,
  },
  reprintingCost: {
    required: true,
  },
  billedCompanyId: {
    required: false,
  },
  visitorBadgePrice: {
    required: true,
  },
  visitorReprintingCost: {
    required: true,
  },
  gcBadgePrintPrice: {
    required: true,
  },
  gcBadgeReprintPrice: {
    required: true,
  },
};

export const SeatBillingModelLabelRules: IFormRules = {
  estimatedWorkersNumber: {
    required: true,
  },
  seatPrice: {
    required: false,
  },
  billedCompanyId: {
    required: false,
  },
  reprintingCost: {
    required: true,
  },
  visitorBadgePrice: {
    required: true,
  },
  visitorReprintingCost: {
    required: true,
  },
};

const validateProjectOwnerAndDeveloper = value => {
  const hasDeveloperProjectOwner = !!value?.find(
    company =>
      (company.role === CompanyRole.DEVELOPER_PROJECT_OWNER && company.name && company.status === CompanyStatus.ACTIVE) ||
      (company.role === CompanyRole.DEVELOPER && company.name && company.status === CompanyStatus.ACTIVE)
  );
  return hasDeveloperProjectOwner;
};

const validateGeneralContractor = value => {
  const hasGeneralContractor = !!value?.find(
    company => company.role === CompanyRole.GENERAL_CONTRACTOR && company.name && company.status === CompanyStatus.ACTIVE
  );
  return hasGeneralContractor;
};

export const AssignClientLabelRules: IFormRules = {
  relatedCompanies: {
    required: true,
  },
};

export const AssignClientDraftRules: IFormRules = {
  ...fieldRules,
  relatedCompanies: {
    required: false,
  },
};

export const AssignClientApprovalRules: IFormRules = {
  ...fieldRules,
  relatedCompanies: {
    required: true,
    rules: [
      ({ value }) => {
        const hasProjectOwnerOrDeveloper = validateProjectOwnerAndDeveloper(value);
        const hasGeneralContractor = validateGeneralContractor(value);
        const errors = [];
        if (!hasProjectOwnerOrDeveloper) {
          errors[CompanyRole.DEVELOPER_PROJECT_OWNER] = 'is required';
          errors[CompanyRole.DEVELOPER] = 'is required';
        }
        if (!hasGeneralContractor) {
          errors[CompanyRole.GENERAL_CONTRACTOR] = 'is required';
        }
        return errors.length > 0 ? errors : (false as false);
      },
    ],
  },
};

export const getAddressesDraftRules = () => ({
  ...fieldRules,
  jobSiteAddress: {
    required: false,
    rules: [],
  },
  badgingSiteAddress: {
    required: false,
    rules: [],
  },
  badgingSiteAddresses: {
    required: false,
    rules: [],
  },
  mailingAddress: {
    required: false,
    rules: [],
  },
  locations: {
    required: false,
    rules: [validateLocationList],
  },
});

export const getAddressesRules = ({
  badgingMatchesJobSite,
  mailingMatchingType,
}: {
  badgingMatchesJobSite: boolean;
  mailingMatchingType: ProjectNewModel.MailingAddressMatchingType;
}) => {
  return {
    ...fieldRules,
    jobSiteAddress: {
      required: true,
      rules: [validateRequiredAddress],
    },
    badgingSiteAddress: {
      required: false,
      rules: [],
    },
    badgingSiteAddresses: {
      required: !badgingMatchesJobSite,
      rules: [badgingMatchesJobSite ? validateAddress : validateAddressList],
    },
    mailingAddress: {
      required: mailingMatchingType === ProjectNewModel.MailingAddressMatchingType.NONE,
      rules: [mailingMatchingType === ProjectNewModel.MailingAddressMatchingType.NONE ? validateRequiredAddress : validateAddress],
    },
    locations: {
      required: true,
      rules: [validateLocationList],
    },
  };
};

export const addressRules = {
  line1: {
    required: false,
    rules: [],
  },
  line2: {
    required: false,
    rules: [],
  },
  city: {
    required: false,
    rules: [],
  },
  countryId: {
    required: false,
    rules: [],
  },
  stateCode: {
    required: false,
    rules: [],
  },
  zipCode: {
    required: false,
    rules: [validateZipCode],
  },
  latitude: {
    required: false,
    rules: [],
  },
  longitude: {
    required: false,
    rules: [],
  },
  attention1: {
    required: false,
    rules: [],
  },
  attention2: {
    required: false,
    rules: [],
  },
};

export const requiredAddressRules = {
  line1: {
    required: true,
    rules: [],
  },
  line2: {
    required: false,
    rules: [],
  },
  city: {
    required: true,
    rules: [],
  },
  countryId: {
    required: true,
    rules: [],
  },
  stateCode: {
    required: true,
    rules: [],
  },
  zipCode: {
    required: true,
    rules: [validateZipCode],
  },
  latitude: {
    required: true,
    rules: [],
  },
  longitude: {
    required: true,
    rules: [],
  },
};

export const AddressesLabelRules: IFormRules = {
  line1: { required: true },
  line2: { required: false },
  country: { required: true },
  countryId: { required: true },
  city: { required: true },
  stateCode: { required: true },
  stateName: { required: false },
  zipCode: { required: true },
  county: { required: false },
  borough: { required: false },
  latitude: { required: false },
  longitude: { required: false },
  attention1: { required: false },
  attention2: { required: false },
  locations: { required: true },
  locationCodes: { required: true },
};

export const CertificationDraftRules = {
  ...fieldRules,
  certificationGroups: {
    required: false,
    rules: [validateCertificationGroupsList],
  },
  trainingGroups: {
    required: false,
    rules: [validateTrainingGroupsList],
  },
  fcaLearningGroups: {
    required: false,
    rules: [validateFcaLearningGroupsList],
  },
};

export const CertificationApprovalRules = {
  ...fieldRules,
  certificationGroups: {
    required: false,
    rules: [validateCertificationGroupsList],
  },
  trainingGroups: {
    required: false,
    rules: [validateTrainingGroupsList],
  },
  fcaLearningGroups: {
    required: false,
    rules: [validateFcaLearningGroupsList],
  },
};

export const BadgeTemplateDraftRules = {
  ...fieldRules,
  badgeTemplates: {
    required: false,
    rules: [],
  },
};

export const BadgeTemplateApprovalRules = {
  ...fieldRules,
  badgeTemplates: {
    required: true,
    rules: [],
  },
};

const validateTurnstileSchedule = ({ value: schedules }: { value: TurnstileScheduleModel.ITurnstileSchedule[] }) => {
  const errorList = schedules?.reduce((acc: any, schedule) => {
    schedule.acsSchedules?.map((slot, slotIndex) => {
      if (
        (slot.projectAcsScheduleTimeSlotId || slot.accessControlSystems?.length > 0) &&
        !(slot.projectAcsScheduleTimeSlotId && slot.accessControlSystems?.length > 0)
      ) {
        if (slot.projectAcsScheduleTimeSlotId) {
          acc[`schedules[${schedule.dayOfWeek}].slots[${slotIndex}].turnstiles`] = 'Please select turnstile.';
        } else {
          acc[`schedules[${schedule.dayOfWeek}].slots[${slotIndex}].slot`] = 'Please select a time slot.';
        }
      }
    });
    return acc;
  }, {});
  return Object.keys(errorList).length > 0 ? errorList : false;
};

export const TurnstileScheduleProjectWizardRules = {
  schedules: {
    required: false,
    rules: [],
  },
};

export const TurnstileScheduleDraftRules = {
  schedules: {
    required: true,
    rules: [validateTurnstileSchedule],
  },
};

export const TurnstileScheduleApprovalRules = {
  schedules: {
    required: true,
    rules: [validateTurnstileSchedule],
  },
};

const validateConsentFormLegals = ({ value: consentFormLegals }: { value: IConsentFormLegal[] }) => {
  const errorList = consentFormLegals.reduce((acc: any, legal, index) => {
    if (!legal?.body || (legal.body.includes('"root":') && !/"text":".+"/.test(legal.body))) {
      acc[`consentFormLegals[${index}].body`] =
        legal.languageCode === 'en' ? 'Please enter English Consent Form Legal' : 'Please enter Spanish Consent Form Legal';
    }
    if (isEmpty(legal.name)) {
      acc[`consentFormLegals[${index}].name`] =
        legal.languageCode === 'en' ? 'Please enter English Consent Form Name' : 'Please enter Spanish Consent Form Name';
    }
    return acc;
  }, {});
  return Object.keys(errorList).length > 0 ? errorList : false;
};

const validateWorkerProfileFields = ({
  value: fields,
  model,
}: {
  value: { workerCustomProfileFields: IWorkerProfileField[]; emailPhoneVerificationType: number };
  model: any;
}) => {
  const errorList = fields.workerCustomProfileFields.reduce((err: any, field, index) => {
    if (
      field.canOverrideSkippableByDefault &&
      (field.isVisible || (field.isVisible === null && field.isVisibleByDefault)) &&
      // (field.isSkippable || (field.isSkippable === null && field.isSkippableByDefault)) &&
      field.isSkippable &&
      field.skippableTill === 0
    ) {
      err[field.fieldId] = 'Please select the Skippable Until';
    }
    return err;
  }, {});
  // if (
  //   fields.emailPhoneVerificationType === 0 &&
  //   model.overrideSetupForAllCompanies &&
  //   fields.workerCustomProfileFields.filter(
  //     cpf =>
  //       (cpf.isVisible === true || (cpf?.isVisible === false ? cpf?.isVisible : cpf?.isVisibleByDefault)) &&
  //       (cpf.isMandatory === true || (cpf?.isMandatory === false ? cpf?.isMandatory : cpf?.isMandatoryByDefault)) &&
  //       (cpf.code === 'WorkerContactEmailOrMobilePhone' || cpf.code === 'WorkerContactEmail' || cpf.code === 'WorkerContactMobileNumber')
  //   ).length <= 0
  // ) {
  //   errorList.WorkerContactEmailOrMobilePhone =
  //     'Email / Mobile Phone / Email or Mobile Phone any one of these fields should be Visible and Mandatory, if 2 Step PIN is selected.';
  // }
  return Object.keys(errorList).length > 0 ? errorList : false;
};

export const ConsentFormLabelRules = {
  name: {
    required: true,
  },
  body: {
    required: true,
  },
};

export const ConsentFormDraftRules = {
  ...fieldRules,
  consentFormFields: {
    required: false,
    rules: [],
  },
  consentFormLegals: {
    required: false,
    rules: [],
  },
};

export const ConsentFormApprovalRules = {
  ...fieldRules,
  consentFormFields: {
    required: false,
    rules: [],
  },
  consentFormLegals: {
    required: true,
    rules: [validateConsentFormLegals],
  },
};

export const pricingAgreementApprovalRules = {
  ...fieldRules,
};

export const WorkerRegistrationDraftRules = {
  workerSettings: {
    required: false,
    rules: [validateWorkerProfileFields],
  },
};

export const WorkerRegistrationApprovalRules = {
  workerSettings: {
    required: false,
    rules: [validateWorkerProfileFields],
  },
};

export const getReviewDraftRules = () => ({
  ...GeneralInformationDraftRules,
  ...AssignClientDraftRules,
  ...getBillingModelDraftRules(),
  ...getAddressesDraftRules(),
  ...CertificationDraftRules,
  ...ConsentFormDraftRules,
});

export const getReviewApprovalRules = ({ billingModelType, badgingMatchesJobSite, mailingMatchingType }) => ({
  ...GeneralInformationApprovalRules,
  ...AssignClientApprovalRules,
  ...getBillingModelApprovalRules({ billingModelType }),
  ...getAddressesRules({ badgingMatchesJobSite, mailingMatchingType }),
  ...CertificationApprovalRules,
  ...ConsentFormApprovalRules,
});

export const communicationModelRules: IFormRules = {
  groupIds: {
    required: true,
  },
  body: {
    required: true,
  },
  communicationResponses: {
    required: false,
    rules: [],
  },
};
